import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { nav } from 'tiny-react-router'
import Splash from '../../shared/components/Splash'
import { CMS_API, formatDate, buildExcerpt, readingTime, getPreviewImage, setMeta } from './utils'

export default function BlogList() {
  const [posts, setPosts] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    setMeta({
      title: 'Bølleblogg',
      description: 'Bloggposter fra Bøllefrø mikrorosteri i Sandnes.',
      type: 'website',
    })
    const load = async () => {
      try {
        const res = await fetch(`${CMS_API}/posts?tag=bollefro&status=published&include=preview`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        setPosts(await res.json())
      } catch (e) {
        setError(e.message)
      }
    }
    load()
  }, [])

  if (error) return <><Splash /><Wrap><h1>Bølleblogg</h1><p>Kunne ikke laste bloggposter. Prøv igjen senere.</p></Wrap></>
  if (!posts) return <><Splash /><Wrap><h1>Bølleblogg</h1><p>Laster…</p></Wrap></>
  if (posts.length === 0) return <><Splash /><Wrap><h1>Bølleblogg</h1><p>Ingen poster ennå. Kom tilbake snart!</p></Wrap></>

  return (
    <>
    <Splash />
    <Wrap>
      <h1>Bølleblogg</h1>
      <Grid>
        {posts.map(post => {
          const img = getPreviewImage(post)
          const excerpt = buildExcerpt(post.blocks)
          const minutes = readingTime(post.blocks)
          const tags = (post.tags || []).filter(t => t !== 'bollefro')
          const goto = () => nav(`/blog/${encodeURIComponent(post.slug)}`)
          return (
            <Card key={post.slug}>
              <CardImage onClick={goto}>
                {img ? <img src={img.url} alt={img.alt || post.title} /> : <Placeholder />}
              </CardImage>
              <Body>
                <h2><Link onClick={goto}>{post.title}</Link></h2>
                <Meta>
                  <span>{formatDate(post.created_at)}</span>
                  <Dot>·</Dot>
                  <span>{minutes} min lesing</span>
                </Meta>
                <Excerpt>{excerpt}</Excerpt>
                {tags.length > 0 && (
                  <Tags>{tags.map(t => <span key={t}>{t}</span>)}</Tags>
                )}
              </Body>
            </Card>
          )
        })}
      </Grid>
    </Wrap>
    </>
  )
}

const Wrap = styled.div`
  padding: 20px;
  text-align: left;
  h1 { margin-bottom: 16px; }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 20px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 20px; }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
`

const CardImage = styled.div`
  display: block;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: transparent;
  line-height: 0;
  cursor: pointer;
  img { width: 100%; height: 100%; object-fit: contain; }
`

const Link = styled.span`
  cursor: pointer;
  &:hover { color: var(--color-splash-purple); }
`

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
`

const Body = styled.div`
  padding: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  h2 {
    margin: 0 0 8px;
    font-size: 1.25em;
    line-height: 1.3;
    color: #333;
  }
`

const Meta = styled.div`
  font-size: 0.8em;
  color: #888;
  margin-bottom: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
`

const Dot = styled.span`
  color: #ccc;
`

const Excerpt = styled.div`
  color: #555;
  font-size: 0.95em;
  line-height: 1.5;
  flex: 1;
`

const Tags = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  span {
    background: var(--color-splash-yellow);
    padding: 2px 8px;
    font-size: 0.75em;
    font-weight: bold;
    border-radius: 3px;
  }
`
